import { booleanAttribute } from "#ce/utils/props";

export const props = Object.freeze({
  /**
   * URL the suggestion data is fetched from. Refetched whenever it changes.
   *
   * @type {String}
   */
  src: {
    type: String,
    required: true,
  },
  /**
   * Placeholder text shown in the empty search input.
   *
   * @type {String}
   * @default undefined
   */
  placeholder: {
    type: String,
    default: undefined,
  },
  /**
   * Body of a function run when a suggestion is chosen, given as a string. It receives the chosen
   * suggestion as `item` and runs with the omnisearch element as `this` — for hosts that cannot
   * attach a listener, such as server-rendered markup.
   *
   * @type {String}
   * @default undefined
   */
  // Vue treats an `onSelect` prop as an event listener, so the name stays lowercase.
  onselect: {
    type: String,
    default: undefined,
  },
  /**
   * Query text used to filter the suggestions.
   *
   * @type {String}
   * @default undefined
   */
  search: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` on the inner `<input>` element.
   *
   * @type {String}
   * @default undefined
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Disable the input. A disabled omnisearch submits no form value.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Make the input readonly.
   *
   * @type {Boolean|String}
   * @default false
   */
  readonly: booleanAttribute(),
  /**
   * URL of the legacy autocomplete script. Supplied by the `SpOmnisearch` defaults in
   * `/sp_components/configuration.json`, not by markup. Accepts a ref so the value can arrive
   * after the element mounts.
   *
   * @type {Object|String}
   */
  autocompleteJsUrl: {
    type: [Object, String],
    required: true,
    external: true,
  },
  /**
   * URL of the legacy autocomplete stylesheet, supplied by the same defaults config as
   * `autocompleteJsUrl`. Consecutive capitals do not survive Vue's attribute hyphenation, so this
   * prop has **no attribute form** — it can only be set as a DOM property.
   *
   * @type {Object|String}
   */
  autocompleteCSSUrl: {
    type: [Object, String],
    required: true,
    external: true,
  },
});
